import React, { useState, useEffect, useCallback } from 'react';
import styles from './ConsumptionAnalysis.module.scss';
import moment from 'moment';
import _ from 'lodash';
import Icon from '../../../components/Icon/Icon';
import GeneralUtils from '../../../utils/GeneralUtils';

import ConsumptionAnalysisActions from '../../../actions/consumptionAnalysisActions';

import { Table, Tabs, Select } from 'antd';
import { DocumentTitle } from '../../../components/DocumentTitle';
import { LogoSpinner } from '../../../components/LogoSpinner';
import { TimeframePicker } from '../../../components/TimeframePicker';

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

const ConsumptionAnalysis = () => {

    const [consumptionData, setConsumptionData] = useState([]);
    const [selectedMeter, setSelectedMeter] = useState(null);
    const [availableMeters, setAvailableMeters] = useState([]);
    const [ts_start, setTsStart] = useState(moment().subtract(1, 'week').startOf('isoWeek').format('YYYY-MM-DD'));
    const [ts_end, setTsEnd] = useState(moment().subtract(1, 'week').startOf('isoWeek').add(6, 'days').format('YYYY-MM-DD'));
    const [loading, setLoading] = useState(false);
    const [activeTabKey, setActiveTabKey] = useState("1");

    const fetchConsumptionData = useCallback(async () => {
        setLoading(true)
        try {
            const data = await ConsumptionAnalysisActions.getConsumptionData(selectedMeter, ts_start, moment(ts_end).add(1, 'day').format('YYYY-MM-DD'));
            setConsumptionData(data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
        } finally {
            setLoading(false);
        }

    }, [ts_start, ts_end, selectedMeter]);

    const fetchMeters = useCallback(async () => {
        try {
            const data = await ConsumptionAnalysisActions.getAvailableMeters(ts_start, moment(ts_end).add(1, 'day').format('YYYY-MM-DD'));
            setAvailableMeters(data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
        }

    }, [ts_start, ts_end])

    useEffect(() => {
        if (ts_start && ts_end && selectedMeter) fetchConsumptionData();
    }, [fetchConsumptionData, ts_start, ts_end, selectedMeter])

    useEffect(() => {
        if (ts_start && ts_end) fetchMeters();
    }, [fetchMeters, ts_end, ts_start])

    const getTimeframePicker = () => {
        return <div className={styles.timeframePickerSection}>
            <div className={styles.timeframePickerHeader}>
                <div className={styles.headerTitle}>Date</div>
            </div>
            <TimeframePicker
                start={ts_start}
                end={ts_end}
                onChange={setTimestamps}
                granularities={['custom', 'day', 'week', 'month', 'year']}
                defaultGranularity='week'
                spacing="10px"
                backgroundColor='#f3f3f3'
                buttonColor='#5e656e'
                disableToday
            />
        </div>;

    };

    const setTimestamps = (start, end, granularity) => {
        const ts_start = moment(start).format('YYYY-MM-DD');
        let ts_end = moment(end).format('YYYY-MM-DD');

        if (moment(ts_end).isAfter(moment(), 'day')) {
            ts_end = moment().format('YYYY-MM-DD');
        }

        setTsStart(ts_start);
        setTsEnd(ts_end);
    }

    const getMeterSelector = () => {
        return <div className={styles.meterPickerSection}>
            <div className={styles.meterPickerHeader}>
                <div className={styles.headerTitle}>Meter</div>
            </div>
            <Select
                options={availableMeters}
                style={{
                    width: '100%',
                    marginTop: '15px'
                }}
                filterSort={(optionA, optionB) => optionA.label.localeCompare(optionB.label)}
                value={selectedMeter}
                onChange={selectMeterHandler}
                placeholder={`Select the Meter`}
                popupMatchSelectWidth={false}
                showSearch
                size={'large'}
                allowClear
                onClear={() => selectMeterHandler(null)}
                filterOption={(input, option) =>
                    (_.get(option, 'label') || '').toLowerCase().includes(input.toLowerCase())
                }
            />
        </div>;

    };

    const selectMeterHandler = (meter) => {
        setConsumptionData([]);
        setSelectedMeter(meter);
    }

    const getTabContent = (type, label, unit) => {
        const columnWidth = 225;
        const columns = [
            { title: 'Time period', dataIndex: 'timePeriod', key: 'timePeriod', width: columnWidth },
            { title: 'Hours', dataIndex: 'hours', key: 'hours', width: columnWidth, render: text => text !== null && text !== undefined ? GeneralUtils.getFormattedNumberWithUnit(text, ' hours', 1) : '-' },
            { title: label, dataIndex: 'current', key: 'current', width: columnWidth, render: text => text !== null && text !== undefined ? GeneralUtils.getFormattedNumberWithUnit(text, unit, 0, 'fr-FR') : '-' },
            { title: `Previous Year`, dataIndex: 'lastYear', key: 'lastYear', width: columnWidth, render: text => text !== null && text !== undefined ? GeneralUtils.getFormattedNumberWithUnit(text, unit, 0, 'fr-FR') : '-' },
            {
                title: `Change`,
                dataIndex: 'change',
                key: 'change',
                width: columnWidth,
                render: (text, record) => {
                    if (text === null || record.lastYear === null || record.current === null) {
                        return '-';
                    }
                    const changeValue = text;
                    const iconName = changeValue > 0 ? 'ArrowUp' : changeValue < 0 ? 'ArrowDown' : null;
                    const color = changeValue > 0 ? 'red' : changeValue < 0 ? 'green' : 'black';
                    const sign = changeValue > 0 ? '+' : ''
                    return (
                        <div style={{ color, display: 'flex', alignItems: 'center' }}>
                            {iconName && <Icon name={iconName} size={20} color={color} style={{ marginLeft: '-6px' }} />} {sign}{GeneralUtils.getFormattedNumberWithUnit(changeValue, unit, 0, 'fr-FR')}
                        </div>
                    );
                }
            },
        ];

        const dataSource = [
            {
                key: '1',
                timePeriod: 'Inside opening hours',
                hours: _.get(consumptionData, 'inside.number_of_hours'),
                current: _.get(consumptionData, `inside.current.${type}`),
                lastYear: _.get(consumptionData, `inside.last_year.${type}`),
                change: _.get(consumptionData, `inside.current.${type}`) || _.get(consumptionData, `inside.last_year.${type}`) ? _.get(consumptionData, `inside.current.${type}`) - _.get(consumptionData, `inside.last_year.${type}`) : null
            },
            {
                key: '2',
                timePeriod: 'Outside opening hours',
                hours: _.get(consumptionData, 'outside.number_of_hours'),
                current: _.get(consumptionData, `outside.current.${type}`),
                lastYear: _.get(consumptionData, `outside.last_year.${type}`),
                change: _.get(consumptionData, `outside.current.${type}`) || _.get(consumptionData, `outside.last_year.${type}`) ? _.get(consumptionData, `outside.current.${type}`) - _.get(consumptionData, `outside.last_year.${type}`) : null
            },
            {
                key: '3',
                timePeriod: 'Total',
                hours: addNonNull(_.get(consumptionData, 'inside.number_of_hours'), _.get(consumptionData, 'outside.number_of_hours')),
                current: addNonNull(_.get(consumptionData, `inside.current.${type}`), _.get(consumptionData, `outside.current.${type}`)),
                lastYear: addNonNull(_.get(consumptionData, `inside.last_year.${type}`), _.get(consumptionData, `outside.last_year.${type}`)),
                change: (_.get(consumptionData, `inside.current.${type}`) || _.get(consumptionData, `outside.current.${type}`)) || (_.get(consumptionData, `inside.last_year.${type}`) || _.get(consumptionData, `outside.last_year.${type}`)) ? (_.get(consumptionData, `inside.current.${type}`) + _.get(consumptionData, `outside.current.${type}`)) - (_.get(consumptionData, `inside.last_year.${type}`) + _.get(consumptionData, `outside.last_year.${type}`)) : null
            }
        ];

        return <div className={styles.tableContainer}>
            <Table columns={columns} dataSource={dataSource} pagination={false} size='small' />
        </div>
    }

    const addNonNull = (...args) => {
        if (args.some(arg => arg === null)) {
            return null;
        }
        return args.reduce((sum, arg) => sum + arg, 0);
    }

    const getGroupedBarChart = () => {
        const activeTypeIndex = parseInt(activeTabKey, 10) - 1;
        const activeType = ['total_consumption', 'total_co2', 'total_cost'][activeTypeIndex];
        const activeUnit = ['kWh', 'kgCO2e', 'Euro'][activeTypeIndex];
        const activeLabel = ['Consumption', 'Emissions', 'Cost'][activeTypeIndex];

        const chartOptions = {
            chart: {
                type: 'column',
                backgroundColor: 'transparent',
                alignTicks: false,
                height: 220
            },
            title: {
                text: null
            },
            plotOptions: {
                column: {
                    pointPadding: 0.1,
                    groupPadding: 0.1,
                    stacking: 'percent'
                }
            },
            exporting: { enabled: false },
            legend: {
                enabled: true,
            },
            credits: { enabled: false },
            xAxis: {
                categories: [activeLabel, 'Hours']
            },
            yAxis: [
                {
                    title: {
                        enabled: false
                    },
                    opposite: false,
                    min: 0,
                    max: 100,
                    labels: {
                        format: '{value}%'
                    }
                },
            ],
            series: [{
                name: `${activeLabel} Outside Opening Hours`,
                data: [_.get(consumptionData, `outside.current.${activeType}`, 0), 0],
                linkedTo: 'outside',
                color: '#1ca28940',
                tooltip: {
                    valueSuffix: ` ${activeUnit}`
                },
                dataLabels: {
                    enabled: true,
                    allowOverlap: true,
                    overflow: 'allow',
                    style: {
                        color: 'black',
                        fontSize: '12px'
                    },
                    formatter: function () {
                        return this.y !== 0 ? this.y.toFixed(0) + ' ' + activeUnit : null;
                    }
                }
            }, {
                name: `${activeLabel} Inside Opening Hours`,
                data: [_.get(consumptionData, `inside.current.${activeType}`, 0), 0],
                linkedTo: 'inside',
                color: '#1ca289',
                tooltip: {
                    valueSuffix: ` ${activeUnit}`
                },
                dataLabels: {
                    enabled: true,
                    allowOverlap: true,
                    overflow: 'allow',
                    style: {
                        color: 'black',
                        fontSize: '12px'
                    },
                    formatter: function () {
                        return this.y !== 0 ? this.y.toFixed(0) + ' ' + activeUnit : null;
                    }
                }
            }, {
                name: 'Outside Opening Hours',
                data: [0, _.get(consumptionData, 'outside.number_of_hours', 0)],
                color: '#1ca28940',
                id: 'outside',
                tooltip: {
                    valueSuffix: ' h',
                    valueDecimals: 1,
                },
                dataLabels: {
                    enabled: true,
                    allowOverlap: true,
                    overflow: 'allow',
                    style: {
                        color: 'black',
                        fontSize: '12px'
                    },
                    formatter: function () {
                        return this.y !== 0 ? (this.y % 1 === 0 ? this.y.toFixed(0) : this.y.toFixed(1)) + ' h' : null;
                    }
                }
            }, {
                name: 'Opening Hours',
                data: [0, _.get(consumptionData, 'inside.number_of_hours', 0)],
                id: 'inside',
                color: '#1ca289',
                tooltip: {
                    valueSuffix: ' h',
                    valueDecimals: 1,
                },
                dataLabels: {
                    enabled: true,
                    allowOverlap: true,
                    overflow: 'allow',
                    style: {
                        color: 'black',
                        fontSize: '12px'
                    },
                    formatter: function () {
                        return this.y !== 0 ? (this.y % 1 === 0 ? this.y.toFixed(0) : this.y.toFixed(1)) + ' h' : null;
                    }
                }
            }]
        };

        return (
            <div className={styles.chartContainer}>
                <HighchartsReact
                    containerProps={{
                        style: { height: '100%', maxHeight: '250px' }
                    }}
                    highcharts={Highcharts}
                    options={chartOptions}
                />
            </div>
        );
    };

    const getTabbedTables = () => {
        const types = ['total_consumption', 'total_co2', 'total_cost'];
        const labels = ['Consumption', 'Emissions', 'Cost'];
        const units = [' kWh', ' kgCO2e', ' Euro'];
        const icons = ['MeterExplorer', 'IndoorConditions', 'Euro'];

        const items = types.map((type, index) => ({
            label: (
                <span>
                    <Icon name={icons[index]} size={16} color={activeTabKey === String(index + 1) ? "#1ca289" : "#5e656e"} style={{ marginRight: '8px' }} />
                    {labels[index]}
                </span>
            ),
            key: String(index + 1),
            children: (
                <div className={`${styles.sectionWrapper}`}>
                    {getTabContent(type, labels[index], units[index])}
                    {getGroupedBarChart()}
                </div>
            )
        }));

        return (
            <div className={styles.tabSection}>
                <Tabs defaultActiveKey="1" activeKey={activeTabKey} onChange={setActiveTabKey} items={items} tabBarStyle={{ marginLeft: '12px', marginRight: '12px', marginBottom: '0px' }} />
            </div>
        );
    };

    return (
        <div
            className={`${styles.consumptionAnalysis} br-mainpanel br-profile-page floorplan-background`}
        >
            <DocumentTitle title="Data Explorer" />
            <LogoSpinner loading={false} />
            <div className='br-container'>
                <div className='row'>
                    <div className='col-12 mg-t-30'>
                        <div className={styles.mainWrapper + " card"}>
                            <DocumentTitle title="Consumption Analysis" />
                            <LogoSpinner loading={loading} />
                            <div className={styles.cardHeader}>
                                <h5 className={styles.cardTitle}>Consumption Analysis</h5>
                            </div>
                            <hr />
                            <div className={styles.cardBody}>
                                <div className='row'>
                                    <div className='col-xl-5 col-6'>
                                        {getMeterSelector()}
                                    </div>
                                    <div className='col-xl-5 col-6'>
                                        {getTimeframePicker()}
                                    </div>
                                    <div className='col-lg-12' >
                                        {(consumptionData.length === 0 || selectedMeter === null) && !loading ? (
                                            <div className={styles.placeholder}>
                                                <p>Select meter to view its consumption data.</p>
                                            </div>
                                        ) : getTabbedTables()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>




    );
};

export default ConsumptionAnalysis;




